import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Map<number, boolean> | undefined} */
  #variantAvailabilityMap;

  connectedCallback() {
    super.connectedCallback();

    this.#buildVariantAvailabilityMap();
    this.addEventListener('change', this.variantChanged.bind(this));
    this.#filterAvailableOptions();
  }

  /**
   * Builds the variant availability map from the JSON data.
   */
  #buildVariantAvailabilityMap() {
    const combinationsScript = this.querySelector('script[data-variant-combinations]');
    if (!combinationsScript) return;

    try {
      const data = JSON.parse(combinationsScript.textContent);
      this.#variantAvailabilityMap = new Map();
      
      if (data.variants) {
        for (const variant of data.variants) {
          this.#variantAvailabilityMap.set(variant.id, variant.available === true);
        }
      }
    } catch (error) {
      console.error('Error parsing variant combinations data:', error);
    }
  }

  /**
   * Filters available options based on currently selected options.
   */
  #filterAvailableOptions() {
    if (!this.#variantAvailabilityMap) return;

    /** @type {HTMLElement[]} */
    const allOptions = Array.from(
      this.querySelectorAll('input[type="radio"]:not([data-sibling-product]), select option')
    );
    /** @type {HTMLElement[]} */
    const selectedOptions = Array.from(
      this.querySelectorAll('input[type="radio"]:checked:not([data-sibling-product]), select option[selected]')
    );
    
    const selectedOptionValueIds = selectedOptions
      .map((opt) => opt.dataset?.optionValueId)
      .filter(Boolean);

    /** @type {Map<string, HTMLElement[]>} */
    const optionsByPosition = new Map();
    for (const option of allOptions) {
      if (!(option instanceof HTMLElement)) continue;
      const position = option.dataset?.optionPosition || option.closest('select')?.id;
      if (!position) continue;
      
      if (!optionsByPosition.has(position)) {
        optionsByPosition.set(position, []);
      }
      const optionsList = optionsByPosition.get(position);
      if (optionsList) {
        optionsList.push(option);
      }
    }

    for (const [position, options] of optionsByPosition) {
      if (!options || options.length === 0) continue;
      const firstOption = options[0];
      if (!firstOption) continue;
      const isSelect = firstOption.tagName === 'OPTION';
      const selectElement = isSelect ? firstOption.closest('select') : null;
      
      const fieldset = firstOption.closest('fieldset');
      const isColorOption = fieldset?.dataset?.isColorFieldset === 'true' || firstOption.dataset?.isColorOption === 'true';
      
      if (!isColorOption) continue;

      const variantMap = this.#variantAvailabilityMap;
      if (!variantMap) continue;

      for (const option of options) {
        if (!(option instanceof HTMLElement)) continue;
        const variantIds = option.dataset?.variantIds;
        if (!variantIds) continue;

        const variantIdArray = variantIds.split(',').map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id));
        
        let hasAvailableCombination = false;

        if (selectedOptionValueIds.length === 0) {
          hasAvailableCombination = variantIdArray.some((variantId) => variantMap.get(variantId) === true);
        } else {
          /** @type {Set<number>[]} */
          const selectedVariantSets = [];
          for (const selectedOption of selectedOptions) {
            const selectedVariantIds = selectedOption.dataset?.variantIds;
            if (selectedVariantIds) {
              selectedVariantSets.push(new Set(
                selectedVariantIds.split(',').map((id) => parseInt(id.trim(), 10)).filter((id) => !isNaN(id))
              ));
            }
          }
          
          if (selectedVariantSets.length > 0) {
            /** @type {Set<number>} */
            let intersection = new Set(selectedVariantSets[0]);
            for (let i = 1; i < selectedVariantSets.length; i++) {
              const currentSet = selectedVariantSets[i];
              if (currentSet) {
                intersection = new Set([...intersection].filter((vid) => currentSet.has(vid)));
              }
            }
            const commonVariants = variantIdArray.filter((variantId) => intersection.has(variantId));
            hasAvailableCombination = commonVariants.some((variantId) => variantMap.get(variantId) === true);
          } else {
            hasAvailableCombination = variantIdArray.some((variantId) => variantMap.get(variantId) === true);
          }
        }

        if (isSelect) {
          if (hasAvailableCombination) {
            option.removeAttribute('disabled');
            option.style.display = '';
          } else {
            option.setAttribute('disabled', 'disabled');
            option.style.display = 'none';
          }
        } else {
          const label = option.closest('label');
          if (label) {
            const serverSideAvailable = option.dataset?.optionAvailable === 'true';
            const input = option instanceof HTMLInputElement ? option : null;
            const isSelected = input?.checked || option.hasAttribute('selected');
            
            if (hasAvailableCombination || serverSideAvailable || isSelected) {
              label.style.display = '';
              label.classList.remove('variant-option--hidden');
            } else {
              label.style.display = 'none';
              label.classList.add('variant-option--hidden');
            }
          }
        }
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const siblingInput = event.target.closest('.variant-option__button-label--sibling')?.querySelector('input');

    if (siblingInput instanceof HTMLInputElement) {
      if (siblingInput.dataset.siblingProductCurrent === 'true') {
        return;
      }

      this.handleSiblingProductNavigation(siblingInput);
      return;
    }

    // Original variant change logic for main product
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    // Filter available options based on new selection
    this.#filterAvailableOptions();

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Handles navigation to sibling products when their swatches are clicked.
   * @param {HTMLInputElement} siblingInput - The clicked sibling swatch input.
   */
  handleSiblingProductNavigation(siblingInput) {
    const siblingProductUrl = siblingInput.dataset.connectedProductUrl;
    const siblingProductTitle = siblingInput.dataset.siblingProductTitle;
    
    if (!siblingProductUrl) {
      console.warn('No sibling product URL found for navigation');
      return;
    }

    // Dispatch a custom event for sibling product navigation
    this.dispatchEvent(new CustomEvent('siblingProductSelected', {
      detail: {
        productUrl: siblingProductUrl,
        productTitle: siblingProductTitle,
        variantId: siblingInput.dataset.variantId,
        optionValueId: siblingInput.dataset.optionValueId
      },
      bubbles: true
    }));

    // Navigate to the sibling product
    window.location.href = siblingProductUrl;
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    // Rebuild variant availability map and filter options after morphing
    this.#buildVariantAvailabilityMap();
    this.#filterAvailableOptions();

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected], fieldset input:checked:not([data-sibling-product])'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked:not([data-sibling-product])')
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}